/**
 * The Wallet-style app bar.
 *
 * TWO MODES, WHICH IS WHAT WALLET ITSELF DOES
 * -------------------------------------------
 * On HOME the bar is a CONTEXT bar: which period, which account. Every figure
 * on the home screen is scoped by that answer, so the answer belongs in the
 * chrome, with chevrons for the motion people repeat all day.
 *
 * On EVERY OTHER SCREEN it collapses to the BRAND plus the four actions, and
 * deliberately draws no page title. Two reasons, and the second is the
 * load-bearing one:
 *
 *   - every screen under this shell already titles itself — the classic pages
 *     through `<PageHeader>`, `@/wallet/stats` and `@/wallet/records` through
 *     their own. A title in the bar would be the same word twice, 40px apart.
 *   - `@/wallet/records` and `@/wallet/stats` each own their own period control
 *     (`LedgerToolbar`, `StatsPeriodBar`) and keep it in the URL, which is the
 *     right call for a screen you deep-link and share. A second, disagreeing
 *     period stepper bolted above them would be a bug you could see. So the
 *     shell yields the period to the screen that already owns it, and keeps its
 *     own context for Home and for any screen that opts in via
 *     `useWalletShell()`.
 *
 * RIGHT SIDE — the four global affordances, in the order the current TopBar
 * already puts them so the muscle memory survives the shell switch: search,
 * hide-values eye, notifications bell, avatar.
 *
 * The bar is CHROME-COLOURED (`--chrome-*`), navy in both themes, which is what
 * the existing top bar and `NotificationBell`'s trigger are already styled for.
 * Reusing it means the bell drops in unmodified and the app still reads as AIM
 * rather than as a different product wearing AIM's data.
 */
import { useLocation } from "react-router-dom";
import Link from "@/compat/link";
import { ChevronDown, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { PrivacyToggle } from "@/lib/privacy/PrivacyToggle";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import BrandMark from "@/components/layout/BrandMark";
import { cn } from "@/lib/utils";
import { Lift } from "@/lib/motion";
import { accountLabel, useWalletShell } from "./context";
import { AvatarMenu } from "./AvatarMenu";
import { HOME_HREF } from "./nav";

const iconBtn =
  "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-[var(--chrome-text-2)] transition-colors hover:bg-[var(--chrome-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--cp-accent)]";

export interface WalletHeaderProps {
  user: { name: string; role: string };
  onOpenContext(): void;
  onOpenSearch(): void;
}

export function WalletHeader({ user, onOpenContext, onOpenSearch }: WalletHeaderProps) {
  const { pathname } = useLocation();
  const { bounds, shiftPeriod, isCurrentPeriod, resetPeriod, accountId, accounts } =
    useWalletShell();

  const contextMode = pathname === HOME_HREF || pathname === "/";
  const selected = accountId ? accounts.find((account) => account.id === accountId) : null;
  const accountText = selected ? accountLabel(selected) : "All accounts";

  return (
    <header className="sticky top-0 z-50 border-b border-[var(--chrome-border)] bg-[var(--chrome-bg)] pt-[env(safe-area-inset-top)]">
      <div className="mx-auto flex h-14 max-w-5xl items-center gap-1 px-2 sm:px-4">
        {/* In context mode the brand only appears where there is room for it —
            on a phone the period is what the bar is for. Off Home it is the
            whole left side, and it doubles as the way back to Home. */}
        {contextMode ? (
          <BrandMark className="hidden h-7 w-7 shrink-0 lg:block" />
        ) : (
          <Link
            href={HOME_HREF}
            className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-1 transition-colors hover:bg-[var(--chrome-hover)]"
          >
            <BrandMark className="h-7 w-7 shrink-0" />
            <span className="text-base font-bold tracking-tight text-white">AIM</span>
          </Link>
        )}

        {contextMode ? (
          <>
            <button
              type="button"
              onClick={() => shiftPeriod(-1)}
              aria-label="Previous period"
              className={iconBtn}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            <button
              type="button"
              onClick={onOpenContext}
              aria-haspopup="dialog"
              className="flex min-w-0 flex-1 flex-col items-center justify-center rounded-md px-2 py-1 transition-colors hover:bg-[var(--chrome-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--cp-accent)]"
            >
              <span className="flex max-w-full items-center gap-1">
                <span className="truncate text-[15px] font-semibold leading-tight text-[var(--chrome-text)]">
                  {bounds.label}
                </span>
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[var(--chrome-text-2)]" />
              </span>
              <span
                className={cn(
                  "max-w-full truncate text-[11px] leading-tight",
                  selected ? "text-[var(--chrome-text)]" : "text-[var(--chrome-text-2)]",
                )}
              >
                {accountText}
              </span>
            </button>

            <button
              type="button"
              onClick={() => shiftPeriod(1)}
              aria-label="Next period"
              className={iconBtn}
            >
              <ChevronRight className="h-5 w-5" />
            </button>

            {/* "Today" only exists when it would do something — a dead button in
                a crowded bar costs more than the shortcut is worth. */}
            {!isCurrentPeriod && (
              <button
                type="button"
                onClick={resetPeriod}
                className="hidden shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-[var(--chrome-text-2)] transition-colors hover:bg-[var(--chrome-hover)] hover:text-[var(--chrome-text)] sm:block"
              >
                Today
              </button>
            )}
          </>
        ) : null}

        <div className="flex shrink-0 items-center gap-0.5 sm:gap-1">
          <Lift rounded="rounded-md">
            <button type="button" onClick={onOpenSearch} aria-label="Search" className={iconBtn}>
              <Search className="h-4 w-4" />
            </button>
          </Lift>
          <Lift rounded="rounded-md">
            <PrivacyToggle variant="chrome" />
          </Lift>
          <Lift rounded="rounded-md">
            <NotificationBell />
          </Lift>
          <Lift rounded="rounded-full">
            <AvatarMenu name={user.name} role={user.role} />
          </Lift>
        </div>
      </div>
    </header>
  );
}
